import { DatePipe, NgTemplateOutlet } from "@angular/common";
import { Component, computed, OnDestroy, OnInit, signal } from "@angular/core";
import { FormControl, ReactiveFormsModule, UntypedFormControl } from "@angular/forms";
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from "@angular/material/autocomplete";
import { MatButtonModule } from "@angular/material/button";
import { MatCardModule } from "@angular/material/card";
import { provideNativeDateAdapter } from "@angular/material/core";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { MatDividerModule } from "@angular/material/divider";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { PageEvent } from "@angular/material/paginator";
import { MatTabsModule } from "@angular/material/tabs";
import { MatTooltipModule } from "@angular/material/tooltip";
import { ActivatedRoute, Router } from "@angular/router";
import { paginatorConfig } from "@app/app.const";
import {
  DateRange,
  DateRangeFilterComponent,
} from "@components/filters/date-range-filter/date-range-filter.component";
import { FilterBarComponent } from "@components/filters/filter-bar/filter-bar.component";
import { DataTableComponent } from "@components/table/data-table/data-table.component";
import {
  TableAction,
  TableColumn,
  TableConfig,
  TableSortEvent,
} from "@components/table/models/table-config.model";
import { IApiResponse } from "@models/api-response.model";
import { IVendor } from "@models/vendors.model";
import { CsvEmailDialogComponent } from "@pages/merchant-orders/csv-email-dialog.component";
import { AppStateService, NotificationService } from "@services/index";
import { RequestService } from "@services/request/request.service";
import { ChartConfiguration, ChartData, ChartType } from "chart.js";
import { BaseChartDirective } from "ng2-charts";
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from "rxjs";
import {
  FormAutocompleteComponent,
  FormAutocompleteOption,
  RefreshButtonComponent,
} from "../../components";

/**
 * The order-value charts are switched between live markets. Exactly one is
 * shown at a time: order values in different currencies cannot be added together.
 */
type ChartMarket = string;

interface MonthlyStatistic {
  month_start: string;
  month_end: string;
  order_count: number;
  total_value: number;
}

interface OrderData {
  order_id: string;
  customer: string;
  merchant: string;
  merchant_first_name?: string;
  vendor_id?: number;
  iso2: number;
  ordered_on: string;
  status: string;
  admin_notes?: string;
  remarks?: string;
  plantsAndLivestock: number;
  others: number;
  total: number;
  totalDtm: number;
  shipping_total: number;
  delivery_remarks?: string;
}

@Component({
  selector: "fh-merchant-orders",
  templateUrl: "./merchant-orders.component.html",
  styleUrls: ["./merchant-orders.component.scss"],
  standalone: true,
  imports: [
    DatePipe,
    NgTemplateOutlet,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    MatTabsModule,
    MatDialogModule,
    MatAutocompleteModule,
    MatTooltipModule,
    MatDividerModule,
    FormAutocompleteComponent,
    DateRangeFilterComponent,
    FilterBarComponent,
    DataTableComponent,
    BaseChartDirective,
    RefreshButtonComponent,
  ],
  providers: [provideNativeDateAdapter()],
})
export class MerchantOrdersComponent implements OnInit, OnDestroy {
  /** Bound wrapper for the refresh button — see `fetchAllData()`. */
  refreshData = (): void => this.fetchAllData();

  readonly paginatorConfig = paginatorConfig;
  private destroy$ = new Subject<void>();

  // State signals
  iso2 = signal<string | null>(null);
  vendorId = signal<number | null>(null);
  pageIndex = signal<number>(0);
  pageSize = signal<number>(paginatorConfig.initialPageSize);
  sortActive = signal<string>("customer");
  sortDirection = signal<"asc" | "desc">("asc");
  currentTab = signal<number>(0);
  dateRange = signal<DateRange>({ start: null, end: null });
  isLoading = signal<boolean>(false);
  /**
   * Market of the order-value charts (24-month performance, top merchants).
   * Separate from the table's optional country filter because a chart of
   * money must always be exactly one market; SG by default.
   */
  get chartMarkets(): string[] {
    return this.appStateService.liveMarketCodes();
  }
  chartMarket = signal<ChartMarket>("SG");
  /** The chart market's currency as the API reports it (country table). */
  chartCurrency = signal<string>("SGD");

  // Data signals
  vendors = signal<IVendor[]>([]);
  orders = signal<OrderData[]>([]);
  total = signal<number>(0);
  totalOrderCount = signal<number>(0);
  monthlyStats = signal<MonthlyStatistic[]>([]);
  filteredMerchants = signal<IVendor[]>([]);

  // Dashboard chart data
  topByVolumeData = signal<ChartData<"bar">>({ labels: [], datasets: [] });
  topByValueData = signal<ChartData<"bar">>({ labels: [], datasets: [] });
  mostProductsData = signal<ChartData<"bar">>({ labels: [], datasets: [] });
  mostCartItemsData = signal<ChartData<"bar">>({ labels: [], datasets: [] });

  // Form controls
  merchantSearchControl = new UntypedFormControl("");

  // Computed
  countries = computed(() => this.appStateService.destinationCountries());
  selectedVendor = computed(() => this.vendors().find((v) => v.id === this.vendorId()));
  selectedVendorName = computed(() =>
    this.vendorId() ? this.selectedVendor()?.name || "All Merchants" : "All Merchants"
  );
  hasDateRange = computed(() => {
    const range = this.dateRange();
    return !!(range.start || range.end);
  });

  countryOptions = computed<FormAutocompleteOption<string>[]>(() => {
    return this.countries().map((country) => ({
      value: country.iso2,
      label: country.name,
      count: country.itemCount,
    }));
  });

  merchantOptions = computed<FormAutocompleteOption<number>[]>(() => {
    return this.filteredMerchants().map((merchant) => ({
      value: merchant.id,
      label: merchant.name,
      count: merchant.order_count,
    }));
  });

  countryFilterFormControl = new FormControl<string | null>(null);

  merchantFilterFormControl = new FormControl<number | null>(null);

  // Merchant performance chart
  merchantPerformanceChartData = computed<ChartData>(() => {
    const stats = this.monthlyStats();
    return {
      labels: stats.map((s) =>
        new Date(s.month_start).toLocaleDateString("en-US", { month: "short", year: "2-digit" })
      ),
      datasets: [
        {
          type: "bar" as const,
          label: "Orders",
          data: stats.map((s) => Number(s.order_count)),
          backgroundColor: "rgba(25, 118, 210, 0.7)",
          yAxisID: "y",
        },
        {
          type: "line" as const,
          label: `Revenue (${this.chartCurrency()})`,
          data: stats.map((s) => Number(s.total_value)),
          borderColor: "#4caf50",
          backgroundColor: "rgba(76, 175, 80, 0.1)",
          borderWidth: 2,
          tension: 0.4,
          fill: true,
          yAxisID: "y1",
        },
      ],
    };
  });

  // Table configuration
  tableConfig = computed<TableConfig<OrderData>>(() => ({
    columns: this.getTableColumns(),
    data: this.orders(),
    total: this.total(),
    loading: this.isLoading(),
    pageSizeOptions: paginatorConfig.pageSizeOptions,
    initialPageSize: paginatorConfig.initialPageSize,
    showFirstLastButtons: paginatorConfig.showFirstLastButtons,
    stickyHeader: true,
    highlightRowOnClick: false,
    rowClickable: false,
    detailsRoute: "/order_details/:order_id",
    getDetailsRouteParams: (row) => ({ order_id: row.order_id }),
  }));

  // The view-details action is now rendered automatically by fh-data-table from
  // config().detailsRoute (a real routerLink, so right-click "open in new tab"
  // works) — this used to duplicate it with a (click)-handler button that couldn't
  // support that.
  tableActions = computed<TableAction<OrderData>[]>(() => []);

  // Chart configs
  barChartType: ChartType = "bar";
  barChartOptions: ChartConfiguration["options"] = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y",
    plugins: { legend: { display: false }, tooltip: { enabled: true } },
    scales: { x: { beginAtZero: true } },
  };

  merchantPerformanceChartOptions = computed<ChartConfiguration["options"]>(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index" as const, intersect: false },
    plugins: { legend: { display: true, position: "top" as const } },
    scales: {
      y: {
        type: "linear" as const,
        display: true,
        position: "left" as const,
        beginAtZero: true,
        title: { display: true, text: "Order Count" },
      },
      y1: {
        type: "linear" as const,
        display: true,
        position: "right" as const,
        beginAtZero: true,
        title: { display: true, text: `Revenue (${this.chartCurrency()})` },
        grid: { drawOnChartArea: false },
      },
    },
  }));

  constructor(
    private readonly auth: RequestService,
    private readonly notificationService: NotificationService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly dialog: MatDialog,
    private readonly appStateService: AppStateService
  ) {
    this.setupMerchantSearch();
  }

  ngOnInit(): void {
    this.getVendors();
    this.watchQueryParams();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onCountryChange(iso2: string | null): void {
    this.updateQueryParams({ iso2: iso2 || undefined, page: 0 });
    this.getVendors();
  }

  /**
   * SG / US behave as one exclusive choice: ticking one unticks the other, and
   * the ticked one cannot be unticked (there is always exactly one market).
   */
  onChartMarketChange(iso2: ChartMarket, event: Event): void {
    const box = event.target as HTMLInputElement;
    if (iso2 === this.chartMarket()) {
      box.checked = true;
      return;
    }
    this.chartMarket.set(iso2);
    if (this.vendorId()) this.fetchMonthlyStats();
    this.fetchDashboardStats();
  }

  onMerchantChange(vendorId: number | null): void {
    this.vendorId.set(vendorId);
    this.updateQueryParams({ vendorId: vendorId || undefined, page: 0 });
    if (vendorId) {
      this.fetchMonthlyStats();
    } else {
      this.monthlyStats.set([]);
    }
  }

  onDateRangeChange(range: DateRange): void {
    this.dateRange.set(range);
    this.updateQueryParams({
      startDate: range.start ? range.start.toISOString() : undefined,
      endDate: range.end ? range.end.toISOString() : undefined,
      page: 0,
    });
  }

  onResetFilters(): void {
    this.merchantSearchControl.reset();
    this.monthlyStats.set([]);
    this.updateQueryParams({
      iso2: undefined,
      vendorId: undefined,
      startDate: undefined,
      endDate: undefined,
      page: 0,
    });
  }

  onTabChange(index: number): void {
    this.currentTab.set(index);
    this.updateQueryParams({ tab: index });
  }

  onSortChange(event: TableSortEvent): void {
    this.sortActive.set(event.prop);
    this.sortDirection.set(event.dir as "asc" | "desc");
    this.updateQueryParams({
      sortBy: event.prop || undefined,
      sortDir: event.dir || undefined,
      page: 0,
    });
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex.set(event.pageIndex);
    this.pageSize.set(event.pageSize);
    this.updateQueryParams({
      page: event.pageIndex,
      pageSize: event.pageSize,
    });
  }

  onMerchantSelected(event: MatAutocompleteSelectedEvent): void {
    const vendor = event.option.value as IVendor;
    this.onMerchantChange(vendor.id);
  }

  clearMerchantSelection(): void {
    this.merchantSearchControl.reset();
    this.onMerchantChange(null);
  }

  displayMerchant(vendor: IVendor): string {
    return vendor ? `${vendor.name} (${vendor.order_count})` : "";
  }

  requestCSVEmail(): void {
    const dialogRef = this.dialog.open(CsvEmailDialogComponent, { width: "400px" });
    dialogRef.afterClosed().subscribe((email: string) => {
      if (!email) return;

      const dateRange = this.normalizeDateRange();
      const data = {
        iso2: this.iso2() || "",
        start: dateRange.start,
        end: dateRange.end,
        vendorId: this.vendorId() || 0,
        sortField: this.sortActive(),
        sortDirection: this.sortDirection(),
        shouldSendEmail: true,
        email,
      };

      this.notificationService.show({
        message: `CSV report will be sent to "${email}"`,
        type: "info",
        duration: 5000,
      });

      this.auth.post<IApiResponse>("/api/v1/merchantOrders", data).subscribe();
    });
  }

  /**
   * Download a CSV of the CURRENTLY-FILTERED orders directly (no email). Re-fetches
   * the full filtered set (not just the current page) using the same filters as the
   * table, then builds the CSV client-side and triggers a browser download.
   */
  downloadCSV(): void {
    const dateRange = this.normalizeDateRange();
    const data = {
      iso2: this.iso2() || "",
      pageIndex: 0,
      // Pull the whole filtered result, not a single page.
      pageSize: this.total() || 10000,
      start: dateRange.start,
      end: dateRange.end,
      vendorId: this.vendorId() || 0,
      sortField: this.sortActive(),
      sortDirection: this.sortDirection(),
    };

    this.isLoading.set(true);
    this.auth.post<IApiResponse>("/api/v1/merchantOrders", data).subscribe({
      next: (res) => {
        this.isLoading.set(false);
        const rows: OrderData[] = res?.result_code === 1 ? res.data.list || [] : [];
        if (!rows.length) {
          this.notificationService.show({
            message: "No orders to export for the current filters.",
            type: "info",
            duration: 4000,
          });
          return;
        }
        this.exportRowsToCsv(rows);
      },
      error: () => {
        this.isLoading.set(false);
        this.notificationService.show({
          message: "Failed to download CSV. Please try again.",
          type: "danger",
          duration: 4000,
        });
      },
    });
  }

  /**
   * Matches the SGT wall-clock rendering of JS's own `Date#toString()` (e.g.
   * "Tue Jun 30 2026 22:23:33 GMT+0800 (Singapore Standard Time)"), but built
   * explicitly from Asia/Singapore parts instead of relying on the admin's browser
   * timezone — order dates are always shown/stored in SGT regardless of where the
   * admin physically is. SGT has no DST, so the "+0800" suffix is always correct.
   */
  private formatOrderedOnLikeJsDateString(iso: string): string {
    const d = new Date(iso);
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: "Asia/Singapore",
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).formatToParts(d);
    const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
    // hour12:false can render midnight as "24" in some engines; normalize to "00".
    const hour = get("hour") === "24" ? "00" : get("hour");
    return `${get("weekday")} ${get("month")} ${get("day")} ${get("year")} ${hour}:${get("minute")}:${get("second")} GMT+0800 (Singapore Standard Time)`;
  }

  private exportRowsToCsv(rows: OrderData[]): void {
    const headers = [
      "order_id",
      "customer",
      "vendor_id",
      "merchant",
      "ordered_on",
      "status",
      "order_status_remarks",
      "remarks",
      "delivery_remarks",
      "total",
      "shipping_total",
      "plantsAndLivestock",
      "others",
    ];
    const esc = (v: unknown): string => {
      const s = v == null ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const num = (v: unknown): string => String(Number(v || 0));
    const lines = [headers.join(",")];
    for (const r of rows) {
      lines.push(
        [
          r.order_id,
          r.customer,
          r.vendor_id ?? "",
          r.merchant_first_name || r.merchant,
          r.ordered_on ? this.formatOrderedOnLikeJsDateString(r.ordered_on) : "",
          r.status,
          r.admin_notes || "",
          r.remarks || "",
          r.delivery_remarks || "",
          num(r.total),
          num(r.shipping_total),
          num(r.plantsAndLivestock),
          num(r.others),
        ]
          .map(esc)
          .join(",")
      );
    }
    // BOM so Excel opens UTF-8 correctly.
    const blob = new Blob([`﻿${lines.join("\n")}`], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const vendor = (this.selectedVendorName?.() || "all-merchants")
      .replace(/[^a-z0-9]+/gi, "-")
      .replace(/^-+|-+$/g, "")
      .toLowerCase();
    a.download = `merchant-orders-${vendor}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  private setupMerchantSearch(): void {
    this.merchantSearchControl.valueChanges
      .pipe(debounceTime(200), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((value) => {
        if (typeof value === "string") {
          this.filterMerchants(value);
        }
      });
  }

  private watchQueryParams(): void {
    this.route.queryParams.subscribe((params) => {
      this.pageIndex.set(parseInt(params.page, 10) || 0);
      this.pageSize.set(parseInt(params.pageSize, 10) || paginatorConfig.initialPageSize);
      this.sortActive.set(params.sortBy || "customer");
      this.sortDirection.set((params.sortDir as "asc" | "desc") || "asc");
      this.iso2.set(params.iso2 || null);
      this.vendorId.set(params.vendorId ? parseInt(params.vendorId, 10) : null);
      this.currentTab.set(parseInt(params.tab, 10) || 0);

      // Set date range
      if (params.startDate && params.endDate) {
        this.dateRange.set({
          start: new Date(params.startDate),
          end: new Date(params.endDate),
        });
      }

      // for the first time when this page loads we need to patch value to form control
      this.countryFilterFormControl.patchValue(this.iso2() || null);
      this.merchantFilterFormControl.patchValue(this.vendorId() || null);

      this.fetchAllData();
    });
  }

  private fetchAllData(): void {
    this.fetchOrders();
    if (this.vendorId()) {
      this.fetchMonthlyStats();
    }
    this.fetchDashboardStats();
  }

  private fetchOrders(): void {
    const dateRange = this.normalizeDateRange();
    const data = {
      iso2: this.iso2() || "",
      pageIndex: this.pageIndex(),
      pageSize: this.pageSize(),
      start: dateRange.start,
      end: dateRange.end,
      vendorId: this.vendorId() || 0,
      sortField: this.sortActive(),
      sortDirection: this.sortDirection(),
    };

    this.isLoading.set(true);
    this.auth.post<IApiResponse>("/api/v1/merchantOrders", data).subscribe({
      next: (res) => {
        if (res.result_code === 1) {
          this.orders.set(res.data.list || []);
          this.total.set(res.data.count || 0);
        }
        this.isLoading.set(false);
      },
      error: () => {
        this.orders.set([]);
        this.total.set(0);
        this.isLoading.set(false);
      },
    });
  }

  private fetchMonthlyStats(): void {
    if (!this.vendorId()) {
      this.monthlyStats.set([]);
      return;
    }

    const iso2 = this.chartMarket();
    this.auth
      .post<IApiResponse>("/api/v1/merchant-monthly-stats", { vendorId: this.vendorId(), iso2 })
      .subscribe((res) => {
        // Ignore a slower reply for a market the reader has switched away from.
        if (res.result_code === 1 && iso2 === this.chartMarket()) {
          this.chartCurrency.set(res.data.currency || iso2);
          this.monthlyStats.set(res.data.monthlyStats || []);
        }
      });
  }

  private fetchDashboardStats(): void {
    // Most-products / most-cart-items keep the page's country filter (it
    // filters vendors by their own country); the order charts use the chart
    // market, and lifetime value is summed in that market too.
    const market = this.chartMarket();
    const data: any = { iso2: this.iso2() || "", marketIso2: market };
    const orderData: any = { iso2: market };

    const range = this.dateRange();
    if (range.start && range.end) {
      data.startDate = orderData.startDate = range.start.toISOString();
      data.endDate = orderData.endDate = range.end.toISOString();
    }

    this.auth
      .post<IApiResponse>("/api/v1/merchant-dashboard/top-by-volume", orderData)
      .subscribe((res) => {
        if (res.result_code === 1 && market === this.chartMarket()) {
          this.topByVolumeData.set({
            labels: res.data.stats.map((s: any) => s.vendor_name),
            datasets: [
              {
                label: "Orders",
                data: res.data.stats.map((s: any) => s.order_count),
                backgroundColor: "#1976d2",
              },
            ],
          });
        }
      });

    this.auth
      .post<IApiResponse>("/api/v1/merchant-dashboard/top-by-value", orderData)
      .subscribe((res) => {
        if (res.result_code === 1 && market === this.chartMarket()) {
          this.chartCurrency.set(res.data.currency || market);
          this.topByValueData.set({
            labels: res.data.stats.map((s: any) => s.vendor_name),
            datasets: [
              {
                label: `Revenue (${res.data.currency || market})`,
                data: res.data.stats.map((s: any) => s.total_value),
                backgroundColor: "#4caf50",
              },
            ],
          });
        }
      });

    this.auth
      .post<IApiResponse>("/api/v1/merchant-dashboard/most-products", data)
      .subscribe((res) => {
        if (res.result_code === 1) {
          this.mostProductsData.set({
            labels: res.data.stats.map((s: any) => s.vendor_name),
            datasets: [
              {
                label: "Products",
                data: res.data.stats.map((s: any) => s.product_count),
                backgroundColor: "#ff9800",
              },
            ],
          });
        }
      });

    this.auth
      .post<IApiResponse>("/api/v1/merchant-dashboard/most-cart-items", data)
      .subscribe((res) => {
        if (res.result_code === 1) {
          this.mostCartItemsData.set({
            labels: res.data.stats.map((s: any) => s.vendor_name),
            datasets: [
              {
                label: "Cart Items",
                data: res.data.stats.map((s: any) => s.cart_item_count),
                backgroundColor: "#e91e63",
              },
            ],
          });
        }
      });
  }

  private getVendors(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.auth
        .get<IApiResponse>(
          `/api/v1/vendor-with-order-count-in-destination-country?iso2=${this.iso2() || ""}`
        )
        .subscribe((res) => {
          if (res.result_code === 1) {
            const sorted = res.data.vendors.sort((a: IVendor, b: IVendor) =>
              a.name.toLowerCase().localeCompare(b.name.toLowerCase())
            );
            this.vendors.set(sorted);
            this.totalOrderCount.set(res.data.totalOrders);
            this.filteredMerchants.set(sorted);
            resolve();
          }
        });
    });
  }

  private filterMerchants(value: string): void {
    const filterValue = value.toLowerCase();
    const filtered = this.vendors().filter((v) => v.name.toLowerCase().includes(filterValue));
    this.filteredMerchants.set(filtered);
  }

  private normalizeDateRange(): { start: string; end: string } {
    const range = this.dateRange();

    // Order dates are always shown/stored in SGT, so the picked calendar day must map
    // to SGT midnight / SGT 23:59:59.999 — not the admin's browser-local midnight
    // (Date#setHours operates in browser-local time, which shifts the whole window
    // whenever the admin isn't physically in UTC+8). Building the offset explicitly
    // from the picked date's Y/M/D avoids depending on the browser's timezone at all.
    const pad = (n: number) => String(n).padStart(2, "0");
    const toSgtStartOfDay = (date: Date): string =>
      `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T00:00:00.000+08:00`;
    const toSgtEndOfDay = (date: Date): string =>
      `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T23:59:59.999+08:00`;

    return {
      start: range.start ? toSgtStartOfDay(new Date(range.start)) : "",
      end: range.end ? toSgtEndOfDay(new Date(range.end)) : "",
    };
  }

  private updateQueryParams(params: any): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: params,
      queryParamsHandling: "merge",
      replaceUrl: true,
    });
  }

  private getTableColumns(): TableColumn<OrderData>[] {
    return [
      {
        key: "order_id",
        header: "Order ID",
        sortable: true,
        width: "150px",
      },
      {
        key: "customer",
        header: "Customer",
        sortable: true,
        width: "180px",
      },
      {
        key: "merchant",
        header: "Merchant",
        sortable: true,
        width: "180px",
      },
      {
        key: "iso2",
        header: "Country",
        sortable: false,
        width: "100px",
      },
      {
        key: "ordered_on",
        header: "Date",
        sortable: true,
        type: "date",
        width: "140px",
      },
      {
        key: "status",
        header: "Status",
        sortable: false,
        width: "120px",
        format: (value) => (value === "in_progress" ? "In Progress" : value),
        cssClass: (row) => `status-${row.status}`,
      },
      {
        key: "plantsAndLivestock",
        header: "Livestock & Plants",
        sortable: false,
        type: "number",
        width: "160px",
        format: (value) => `$${Number(value).toFixed(2)}`,
      },
      {
        key: "others",
        header: "Others",
        sortable: false,
        type: "number",
        width: "120px",
        format: (value) => `$${Number(value).toFixed(2)}`,
      },
      {
        key: "total",
        header: "Total",
        sortable: false,
        type: "number",
        width: "120px",
        format: (value) => `$${Number(value).toFixed(2)}`,
      },
      {
        key: "totalDtm",
        header: "DTM",
        sortable: false,
        type: "number",
        width: "120px",
        format: (value) => `$${Number(value || 0).toFixed(2)}`,
      },
      {
        key: "shipping_total",
        header: "Shipping",
        sortable: false,
        type: "number",
        width: "120px",
        format: (value) => `$${Number(value).toFixed(2)}`,
      },
      {
        key: "delivery_remarks",
        header: "Delivery Remarks",
        sortable: false,
        width: "200px",
        format: (value) => {
          if (!value) return "—";
          return value.length > 40 ? `${value.slice(0, 40)}...` : value;
        },
      },
    ];
  }
}
